// @ts-check
// Barra de ações da seleção da tabela (doc 06, seção 13; doc 15, ações RECORD_SELECTION): atualizar campo, atribuir
// responsável, mudar etapa, mesclar, exportar, favoritar e excluir os registros selecionados. Quais ações aparecem vem
// do registro de comandos (client/src/lib/command-registry.js), o mesmo do menu de comandos e do clique direito.
import { html } from 'htm/preact';
import { useEffect, useRef, useState } from 'preact/hooks';
import { fieldsById, hasCurrentPermissionFlag, isCoordinationFocusedState } from '../../data/metadata-store.js';
import { FIELD_TYPE, isManyToOneRelation } from '../../lib/field-metadata.js';
import { buildSelectionCommands } from '../../lib/command-registry.js';
import { Icon } from '../../ui/icon.js';
import { Tag } from '../../ui/chip.js';
import { Dropdown, DropdownMenuItems, MenuItem } from '../../ui/dropdown.js';
import { Modal } from '../../ui/modal.js';
import { showErrorSnackbar } from '../../ui/snackbar.js';
import { openMergeRecordsPanel, openUpdateRecordsPanel } from '../side-panel/side-panel-state.js';
import { getBulkEditableFields } from '../side-panel/update-records-panel.js';
import { t, translateMetadataLabel } from '../../i18n/index.js';
import { classifyRecordsAsNotLead, supportsNotLead } from './not-lead.js';
import { RecordSelectionForCommands, SelectionAutomationMenuItems } from '../automations/manual-automations.js';
import { deleteRecordSelection, exportRecordSelection, favoriteRecordSelection } from '../command-menu/selection-actions.js';
import { activeSelectionTargetState } from '../command-menu/command-state.js';

/**
 * @typedef {import('../../lib/field-metadata.js').ObjectMetadata} ObjectMetadata
 * @typedef {import('../../lib/field-metadata.js').FieldMetadata} FieldMetadata
 * @typedef {import('../command-menu/command-state.js').SelectionTarget} SelectionTarget
 */

const OWNER_FIELD_NAMES = ['owner', 'assignee', 'accountOwner', 'host', 'coordinator', 'manager'];

/**
 * Campo de responsável: uma relação com membro da equipe, preferindo os nomes usuais do pacote.
 * @param {ObjectMetadata} objectMetadata
 */
export function findOwnerField(objectMetadata) {
  const editable = getBulkEditableFields(objectMetadata).filter((field) => isManyToOneRelation(field) && field.relation?.targetObjectNameSingular === 'workspaceMember');
  return OWNER_FIELD_NAMES.map((name) => editable.find((field) => field.name === name)).find(Boolean) ?? editable[0] ?? null;
}

/**
 * Campo de etapa: o agrupamento da view quando é SELECT, senão `stage` ou `status`.
 * @param {ObjectMetadata} objectMetadata @param {import('../../data/metadata-store.js').View} view
 */
export function findStageField(objectMetadata, view) {
  const editable = getBulkEditableFields(objectMetadata).filter((field) => field.type === FIELD_TYPE.SELECT);
  const groupBy = view.mainGroupByFieldMetadataId ? fieldsById.value.get(view.mainGroupByFieldMetadataId) : null;
  return editable.find((field) => field.id === groupBy?.id) ?? editable.find((field) => field.name === 'stage') ?? editable.find((field) => field.name === 'status') ?? null;
}

/**
 * Publica a seleção para o menu de comandos (Cmd+K) enquanto a barra está aberta, sempre com as funções mais recentes
 * (a tabela recria as funções a cada render).
 * @param {Omit<SelectionTarget, 'count' | 'objectMetadata'> & { objectMetadata: ObjectMetadata, count: number }} target
 */
export function usePublishedSelectionTarget(target) {
  const latest = useRef(target);
  latest.current = target;
  useEffect(() => {
    /** @type {SelectionTarget} */
    const published = {
      objectMetadata: target.objectMetadata,
      count: target.count,
      isTrashMode: target.isTrashMode,
      get view() { return latest.current.view; },
      get isAllMatchingSelected() { return latest.current.isAllMatchingSelected; },
      get stageOptions() { return latest.current.stageOptions; },
      get onMoveToStage() { return latest.current.onMoveToStage; },
      resolveSelectedIds: () => latest.current.resolveSelectedIds(),
      onChanged: () => latest.current.onChanged(),
      onDeleted: (ids) => latest.current.onDeleted(ids),
      onClear: () => latest.current.onClear(),
    };
    activeSelectionTargetState.value = published;
    return () => {
      if (activeSelectionTargetState.peek() === published) activeSelectionTargetState.value = null;
    };
  }, [target.objectMetadata, target.count, target.isTrashMode]);
}

/**
 * @param {{ objectMetadata: ObjectMetadata, view: import('../../data/metadata-store.js').View, selectedCount: number,
 *   resolveSelectedIds: () => Promise<string[]>, isAllMatchingSelected: boolean, onClear: () => void, onChanged: () => void,
 *   onDeleted: (ids: string[]) => void, selectAllPrompt?: any,
 *   stageOptions?: { value: string | null, label: string, color: string, isHandoff?: boolean }[] | null, onMoveToStage?: ((value: string | null) => Promise<void>) | null }} props
 *   stageOptions e onMoveToStage: no kanban a etapa é escolhida aqui e cada card passa pelo diálogo da etapa
 */
export function RecordSelectionActions({ objectMetadata, view, selectedCount, resolveSelectedIds, isAllMatchingSelected, onClear, onChanged, onDeleted, selectAllPrompt, stageOptions = null, onMoveToStage = null }) {
  const moreRef = useRef(/** @type {HTMLButtonElement | null} */ (null));
  const stageRef = useRef(/** @type {HTMLSpanElement | null} */ (null));
  const [isMoreOpen, setIsMoreOpen] = useState(false);
  const [isStageOpen, setIsStageOpen] = useState(false);
  const [isConfirmingDelete, setIsConfirmingDelete] = useState(false);
  const [isBusy, setIsBusy] = useState(false);
  const ownerField = findOwnerField(objectMetadata);
  const stageField = findStageField(objectMetadata, view);
  const objectLabel = translateMetadataLabel(objectMetadata.labelPlural);
  // o papel do membro decide o que aparece; o mesmo registro alimenta o menu de comandos e o clique direito
  const available = new Set(buildSelectionCommands({
    page: 'recordIndex',
    hasPermissionFlag: hasCurrentPermissionFlag,
    isCoordinationFocused: isCoordinationFocusedState.value,
    object: {
      nameSingular: objectMetadata.nameSingular, namePlural: objectMetadata.namePlural, labelSingular: objectLabel, labelPlural: objectLabel,
      isSystem: Boolean(objectMetadata.isSystem), duplicateCriteria: objectMetadata.duplicateCriteria ?? null, permissions: objectMetadata.permissions,
    },
    view,
    selection: { count: selectedCount, hasOwnerField: Boolean(ownerField), hasStageField: Boolean(stageField), supportsNotLead: supportsNotLead(objectMetadata) },
  }).map((command) => command.id));
  usePublishedSelectionTarget({
    objectMetadata, view, count: selectedCount, isTrashMode: false, isAllMatchingSelected, resolveSelectedIds, onChanged, onDeleted, onClear, stageOptions, onMoveToStage,
  });

  const withIds = async (/** @type {(ids: string[]) => void | Promise<void>} */ action) => {
    setIsMoreOpen(false);
    setIsBusy(true);
    try {
      await action(await resolveSelectedIds());
    } catch (error) {
      showErrorSnackbar(error);
    } finally {
      setIsBusy(false);
    }
  };

  const openUpdate = (/** @type {FieldMetadata | null} */ presetField) => withIds((ids) => openUpdateRecordsPanel({
    objectNameSingular: objectMetadata.nameSingular, ids, presetFieldName: presetField?.name ?? null, onDone: onChanged,
  }));

  const exportSelection = () => withIds((ids) => exportRecordSelection({ objectMetadata, view, ids, isAllMatchingSelected }));
  const favoriteSelection = () => withIds((ids) => favoriteRecordSelection({ objectMetadata, ids }));
  const deleteSelection = () => {
    setIsConfirmingDelete(false);
    return withIds((ids) => deleteRecordSelection({ objectMetadata, ids, onDeleted, onChanged }));
  };

  const button = (/** @type {string} */ icon, /** @type {string} */ label, /** @type {() => void} */ onClick, /** @type {string} */ testId) =>
    html`<button type="button" class="light-button selection-action" data-testid=${testId} disabled=${isBusy} onClick=${onClick}>
      <${Icon} name=${icon} size=${14} /><span>${label}</span>
    </button>`;

  return html`<div class="table-selection-bar" role="toolbar" aria-label=${t('parity.bulk.toolbar')}>
    <span>${t('table.selectedCount', { count: selectedCount })}</span>
    ${selectAllPrompt ?? null}
    ${available.has('selection.update') ? button('IconEdit', t('parity.bulk.update'), () => openUpdate(null), 'bulk-update') : null}
    ${available.has('selection.assign') ? button('IconUserCircle', t('parity.bulk.assign'), () => openUpdate(ownerField), 'bulk-assign') : null}
    ${available.has('selection.changeStage') && stageField && stageOptions && onMoveToStage
      ? html`<span ref=${stageRef} class="selection-stage-anchor">
          ${button('IconProgressCheck', t('parity.bulk.changeStage', { field: translateMetadataLabel(stageField.label) }), () => setIsStageOpen(!isStageOpen), 'bulk-stage')}
          <${Dropdown} anchorRef=${stageRef} isOpen=${isStageOpen} onClose=${() => setIsStageOpen(false)} placement="top-start" width=${240}>
            <${DropdownMenuItems} hasMaxHeight=${true}>
              ${stageOptions.map((option) => html`<${MenuItem}
                key=${option.value ?? ''}
                leftComponent=${html`<${Tag} color=${option.color} text=${option.label} variant=${option.value === null ? 'outline' : 'solid'} />`}
                text=""
                rightComponent=${option.isHandoff ? html`<${Icon} name="IconLogout" size=${14} />` : undefined}
                onClick=${() => {
                  setIsStageOpen(false);
                  setIsBusy(true);
                  onMoveToStage(option.value).catch(showErrorSnackbar).finally(() => setIsBusy(false));
                }}
              />`)}
            <//>
          <//>
        </span>`
      : available.has('selection.changeStage') && stageField ? button('IconProgressCheck', t('parity.bulk.changeStage', { field: translateMetadataLabel(stageField.label) }), () => openUpdate(stageField), 'bulk-stage') : null}
    ${available.has('selection.notLead')
      ? button('IconUserOff', t('notLead.action'), () => withIds(async (ids) => {
          if (await classifyRecordsAsNotLead(objectMetadata, ids)) onChanged();
        }), 'bulk-not-lead')
      : null}
    <button ref=${moreRef} type="button" class="light-button selection-action" aria-label=${t('parity.bulk.more')} disabled=${isBusy} onClick=${() => setIsMoreOpen(!isMoreOpen)}>
      <${Icon} name="IconDots" size=${14} />
    </button>
    <${Dropdown} anchorRef=${moreRef} isOpen=${isMoreOpen} onClose=${() => setIsMoreOpen(false)} placement="top-end" width=${220}>
      <${DropdownMenuItems}>
        <${SelectionAutomationMenuItems} objectNameSingular=${objectMetadata.nameSingular} selectedCount=${selectedCount} resolveSelectedIds=${resolveSelectedIds}
          onDone=${onChanged} onPick=${() => setIsMoreOpen(false)} />
        ${available.has('selection.merge') ? html`<${MenuItem} icon="IconArrowMerge" text=${t('parity.merge.action', { object: objectLabel })} onClick=${() => withIds((ids) => openMergeRecordsPanel({ objectNameSingular: objectMetadata.nameSingular, ids, onDone: onChanged }))} />` : null}
        ${available.has('selection.export') ? html`<${MenuItem} icon="IconFileExport" text=${t('parity.bulk.export')} onClick=${exportSelection} />` : null}
        ${available.has('selection.favorite') ? html`<${MenuItem} icon="IconHeart" text=${t('parity.favorites.add')} onClick=${favoriteSelection} />` : null}
        ${available.has('selection.delete')
          ? html`<${MenuItem} icon="IconTrash" accent="danger" text=${t('parity.bulk.delete')} onClick=${() => {
              setIsMoreOpen(false);
              setIsConfirmingDelete(true);
            }} />`
          : null}
      <//>
    <//>
    <button type="button" class="light-button" onClick=${onClear}>${t('table.clearSelection')}</button>
    <${RecordSelectionForCommands} objectNameSingular=${objectMetadata.nameSingular} selectedCount=${selectedCount} resolveSelectedIds=${resolveSelectedIds} onDone=${onChanged} />
    ${isConfirmingDelete
      ? html`<${Modal}
          title=${t('parity.bulk.confirmDeleteTitle', { count: selectedCount })}
          subtitle=${t('parity.bulk.confirmDeleteSubtitle')}
          confirmLabel=${t('parity.bulk.delete')}
          confirmAccent="danger"
          onClose=${() => setIsConfirmingDelete(false)}
          onConfirm=${deleteSelection}
        />`
      : null}
  </div>`;
}
